import React, { useState, useRef } from 'react';
import { Rule, JsonLogicRule } from '../types';
import { Upload, Download, FileJson } from 'lucide-react';
import { exportRuleToJson, importRuleFromJson } from '../utils/jsonLogicConverter';

interface ImportExportProps {
  rules: Rule[];
  onImport: (rules: Rule[]) => void;
}

const ImportExport: React.FC<ImportExportProps> = ({ rules, onImport }) => {
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExportAll = () => {
    const jsonRules = rules.map(rule => exportRuleToJson(rule));
    const dataStr = JSON.stringify(jsonRules, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `all-rules-${Date.now()}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const parsed = JSON.parse(content);
        
        let importedRules: Rule[] = [];
        
        if (Array.isArray(parsed)) {
          importedRules = parsed.map((jsonRule: JsonLogicRule) => importRuleFromJson(jsonRule));
        } else if (typeof parsed === 'object' && parsed !== null) {
          importedRules = [importRuleFromJson(parsed as JsonLogicRule)];
        } else {
          throw new Error('Invalid JSON format');
        }

        onImport(importedRules);
        setImportSuccess(`Successfully imported ${importedRules.length} rule(s)`);
        setImportError(null);
        
        setTimeout(() => setImportSuccess(null), 3000);
      } catch (error) {
        setImportError(`Failed to import rules: ${error instanceof Error ? error.message : 'Unknown error'}`);
        setImportSuccess(null);
      }
    };

    reader.readAsText(file);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleExportTemplate = () => {
    const template: JsonLogicRule = {
      filters: {
        and: [
          { '==': [{ var: 'breed' }, 'labrador_retriever'] },
          { '>=': [{ var: 'age' }, 7] }
        ]
      },
      triggers: {
        or: [
          { '>': [{ var: 'temperature' }, 30] },
          { '==': [{ var: 'season' }, 'summer'] }
        ]
      },
      notification: {
        id: 'template-1',
        topic: 'Heat Warning',
        body: 'Your senior dog needs extra care in hot weather. Ensure plenty of water and shade.',
        priority: 'high',
        channels: ['push', 'email']
      },
      metadata: {
        id: 'template-rule-1',
        name: 'Senior Dog Heat Alert',
        description: 'Alert for senior dogs during hot weather',
        active: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    };

    const dataStr = JSON.stringify(template, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', 'rule-template.json');
    linkElement.click();
  };

  return (
    <div className="import-export">
      <div className="import-export-actions">
        <div className="action-group">
          <h3>Import Rules</h3>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleImport}
            style={{ display: 'none' }}
            id="import-file"
          />
          <label htmlFor="import-file" className="btn-secondary">
            <Upload size={16} />
            Import from JSON
          </label>
          
          {importError && (
            <div className="alert alert-error">
              {importError}
            </div>
          )}
          
          {importSuccess && (
            <div className="alert alert-success">
              {importSuccess}
            </div>
          )}
        </div>

        <div className="action-group">
          <h3>Export Rules</h3>
          <button 
            onClick={handleExportAll} 
            className="btn-secondary"
            disabled={rules.length === 0}
          >
            <Download size={16} />
            Export All Rules ({rules.length})
          </button>
        </div>

        <div className="action-group">
          <h3>Template</h3>
          <button onClick={handleExportTemplate} className="btn-secondary">
            <FileJson size={16} />
            Download Template
          </button>
        </div>
      </div>

      <div className="format-info">
        <h4>JSON Logic Format</h4>
        <p>Rules are exported in JSON Logic format, making them portable and evaluable.</p>
        <details>
          <summary>View Example Format</summary>
          <pre>{`{
  "filters": {
    "and": [
      { "==": [{ "var": "breed" }, "labrador_retriever"] },
      { ">=": [{ "var": "age" }, 7] }
    ]
  },
  "triggers": {
    "or": [
      { ">": [{ "var": "temperature" }, 30] },
      { "==": [{ "var": "weather" }, "sunny"] }
    ]
  },
  "notification": {
    "topic": "Heat Alert",
    "body": "High temperature warning for your dog",
    "priority": "high",
    "channels": ["push", "email"]
  },
  "metadata": {
    "name": "Heat Warning Rule",
    "description": "Alerts when temperature is too high",
    "active": true
  }
}`}</pre>
        </details>
      </div>
    </div>
  );
};

export default ImportExport;